import { normalizeAssistantText } from '@/lib/assistantFreeText';
import {
  bestCompositeAddOnMatch,
  bestCompositeProductMatch,
  collectMenuAddOns,
  expandCatalogForCompositeOrder,
  expandProductsForCompositeOrder,
  looksLikeMenuAddOnLine,
  productsOfferingAddOn,
} from '@/lib/assistant/assistantProductSearch';
import { extractCustomizationNote } from '@/lib/assistant/assistantProducts';
import {
  applyRegionalOrderSpanish,
  hasCompositeOrderIntent,
  ORDER_VERB_PREFIX_RX,
  ADDRESS_EXPLICIT_RX,
  DRINK_SIDE_HINTS_RX,
  stripOrderGreeting,
  parseRegionalCashAmount,
} from '@/lib/assistant/assistantRegionalSpanish';
import { selectionToCanonicalPayment } from '@/lib/checkout/paymentMethods';
import { stripTipClause, trimOrphanTrailingCon } from '@/lib/assistant/parseOrderTip';
import { normalizeOrderQueryPhrases } from '@/lib/assistant/orderProductQuery';
import { findTrailingAddressInText } from '@/lib/orders/orderAddressParse';

const STAFF_NEWLINE_PLACEHOLDER = '\u0001';

function preserveStaffNewlines(text) {
  return String(text || '').replace(/\r\n/g, '\n').replace(/\n/g, STAFF_NEWLINE_PLACEHOLDER);
}

function restoreStaffNewlines(text) {
  return String(text || '').replace(new RegExp(STAFF_NEWLINE_PLACEHOLDER, 'g'), '\n');
}

const DRINK_SIDE_HINTS = DRINK_SIDE_HINTS_RX;
/** Línea que suena a producto distinto (evita fusionar "kids grill" con la burger anterior). */
const PRODUCT_LINE_HINT_RX =
  /\b(kids|grill|bigmati|big|smash|classic|nugget|papas|bebida|lata|extra|carne|doble|simple|combo|hamburguesa|burger|nugget|pizza|piezas|pieza|familiar|grupo)\b/i;
const NEW_ITEM_PREFIX = /^(\d+|un|una|unos|unas|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\b/i;
const WORD_QTY = {
  un: 1,
  uno: 1,
  una: 1,
  unos: 1,
  unas: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
};
/** Cantidad en palabras + producto encadenado ("dos kids", "3 latas"). */
const CHAINED_QTY_PRODUCT_RX =
  /(?:dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|un|una|unos|unas|\d{1,2})\s+(?:sin\b|kids\b|latas?\b|bebidas?\b|gaseosas?\b|nugget|bigmati|smash|papas\b|hamburguesa)/i;
const CHAINED_SPLIT_RX =
  /\s+(?=(?:dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|una|un|unos|unas|\d{1,2})\s+(?:sin\b|latas?\b|bebidas?\b|gaseosas?\b|kids\b|nugget|bigmati|smash|papas\b|hamburguesa))/i;
/** Nueva línea o coma antes de cantidad → otro ítem del pedido. */
const LIST_ITEM_SPLIT_RX =
  /\s*(?:,\s*(?=\d{1,2}\s*[xX×]?\s*|\d{1,2}\s+[a-záéíóúñ]|\b(?:un|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\s+[a-záéíóúñ])|\s+(?:y|mas|más|plus|\+|o)\s+(?=\d{1,2}\s*[xX×]?\s*|\d{1,2}\s+[a-záéíóúñ]|\b(?:un|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\s+[a-záéíóúñ]|\d{1,3}\s*piezas?\b|\bpizza\b|\bbig\b|\bkids\b))/i;
const EMBEDDED_QTY_SPLIT_RX =
  /\s+(?=(?:\d{1,2}\s*[xX×]?\s*|\d{1,2}\s+(?!pesos|lucas|mil\b|propina\b)|\b(?:dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\s+[a-záéíóúñ]))/i;

const ORDER_TYPO_FIXES = [
  [/\bhamrguesa\b/gi, 'hamburguesa'],
  [/\bhambuguesa\b/gi, 'hamburguesa'],
  [/\bhambruguesa\b/gi, 'hamburguesa'],
  [/\befectvio\b/gi, 'efectivo'],
  [/\btransfernecia\b/gi, 'transferencia'],
  [/\bpepinillosmas\b/gi, 'pepinillos mas'],
  [/\bpepinillomas\b/gi, 'pepinillos mas'],
  [/\bpapasy\b/gi, 'papas y'],
  [/\b([a-záéíóúñ]{4,})(mas|más)(\s+(?:un|una|unos|unas|\d))/gi, '$1 $2$3'],
];

/** Normaliza texto de pedido antes de parsear (typos, modismos, "mas" pegado, etc.). */
export function normalizeCompositeOrderInput(text, options = {}) {
  let t = String(text || '').trim();
  t = t.replace(/\r\n/g, '\n');
  if (options.staffOrder) {
    return t
      .split('\n')
      .map((line) => {
        let l = stripOrderGreeting(line);
        l = applyRegionalOrderSpanish(l);
        l = normalizeOrderQueryPhrases(l);
        for (const [rx, rep] of ORDER_TYPO_FIXES) {
          l = l.replace(rx, rep);
        }
        return normalizeMidAddClauses(l.replace(/\s+/g, ' ').trim());
      })
      .filter(Boolean)
      .join('\n');
  }
  t = t.replace(/\n+/g, ', ');
  t = stripOrderGreeting(t);
  t = applyRegionalOrderSpanish(t);
  t = normalizeOrderQueryPhrases(t);
  for (const [rx, rep] of ORDER_TYPO_FIXES) {
    t = t.replace(rx, rep);
  }
  return normalizeMidAddClauses(t.replace(/\s+/g, ' ').trim());
}

/** Convierte "… y agrego 1 coca" en "… y 1 coca" para partir segmentos. */
export function normalizeMidAddClauses(text) {
  let t = String(text || '').trim();
  const addVerbRx = /\s+(agrego|agrega|añade|sumame|sumá|sumale|metele|ponle|echate)\s+/gi;
  const lead = t.match(/^(agrego|agrega|añade|sumame|sumá|sumale|metele|ponle|echate)\s+/i);
  if (lead) {
    const rest = t.slice(lead[0].length).replace(addVerbRx, ' y ');
    return `${lead[0]}${rest}`.replace(/\s+/g, ' ').trim();
  }
  return t.replace(addVerbRx, ' y ').replace(/\s+/g, ' ').trim();
}

function parseCashAmountFromText(text) {
  const regional = parseRegionalCashAmount(text);
  if (regional != null) return regional;
  const raw = String(text || '');
  const patterns = [
    /\b(?:pago|abono)\s+(?:en|con)\s+(?:efectivo\s+)?(?:con|en)\s+\$?\s*([\d][\d.,]*)\b/i,
    /\b(?:pago|abono)\s+(?:en|con)\s+\$?\s*([\d][\d.,]*)\b/i,
    /\befectivo\s+(?:con|en)\s+\$?\s*([\d][\d.,]*)\b/i,
    /\b(?:con|en)\s+\$?\s*([\d][\d.,]{2,})\s*(?:pesos|mil|lucas)?\b/i,
  ];
  for (const rx of patterns) {
    const m = raw.match(rx);
    if (!m) continue;
    const digits = String(m[1] || '').replace(/\./g, '').replace(',', '.');
    const n = Number(digits);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  return null;
}

function stripPaymentClause(text) {
  const raw = String(text || '').trim();
  const cashAmount = parseCashAmountFromText(raw);
  const n = normalizeAssistantText(raw);

  const trailingPay = raw.match(
    /\b(?:en|con)\s+(efectivo|transferencia|transfer|debito|débito|tarjeta|mercadopago|mercado\s+pago)\s*[,;.]?\s*$/i
  );
  if (trailingPay) {
    const token = trailingPay[1].toLowerCase();
    const selection = token.includes('transfer')
      ? 'transferencia'
      : token.includes('efect')
        ? 'efectivo'
        : token.includes('debit') || token.includes('tarj')
          ? 'online_card'
          : token.includes('mercado')
            ? 'online_mp'
            : null;
    const rest = raw.slice(0, trailingPay.index).trim().replace(/[,;]\s*$/, '');
    return { rest: trimOrphanTrailingCon(rest), paymentSelection: selection, cashAmount };
  }

  const payRx =
    /\b(pago|pagar|pago con|pago en|abono con|abono en)\s+(efectivo|transferencia|transfer|debito|débito|tarjeta|mercadopago|mercado pago|plata|guita|cash)\b/i;
  const m = n.match(payRx);
  if (!m) {
    const loose =
      /\b(transferencia|transfer|debito|débito|tarjeta|mercadopago|mercado pago)\b/i.test(n) &&
      /\b(pago|pagar|abono)\b/i.test(n);
    if (!loose) return { rest: trimOrphanTrailingCon(raw), paymentSelection: null, cashAmount };
    const selection = n.includes('transfer')
      ? 'transferencia'
      : n.includes('debit') || n.includes('tarj')
        ? 'online_card'
        : n.includes('mercado')
          ? 'online_mp'
          : null;
    let rest = raw
      .replace(/\b(?:pago|pagar|abono)\s+(?:con|en)\s*/gi, '')
      .replace(/\b(?:transferencia|transfer|debito|débito|tarjeta|mercadopago|mercado pago)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (cashAmount != null) {
      rest = rest
        .replace(
          /\b(?:con|en)\s+\$?\s*[\d][\d.,]*(?:\s*(?:lucas?|gambas?|gamba|pesos|mil|chirolas?|mangos?|billetes?))?\b/gi,
          ''
        )
        .replace(/\s+/g, ' ')
        .trim();
    }
    return { rest: trimOrphanTrailingCon(rest), paymentSelection: selection, cashAmount };
  }
  const selection =
    m[2].includes('transfer')
      ? 'transferencia'
      : m[2].includes('efect')
        ? 'efectivo'
        : m[2].includes('debit') || m[2].includes('tarj')
          ? 'online_card'
          : m[2].includes('mercado')
            ? 'online_mp'
            : null;
  let rest = raw
    .replace(new RegExp(m[0], 'i'), '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[,;]+\s*$/, '');
  if (cashAmount != null) {
    rest = rest
      .replace(
        /\b(?:con|en)\s+\$?\s*[\d][\d.,]*(?:\s*(?:lucas?|gambas?|gamba|pesos|mil|chirolas?|mangos?|billetes?))?\b/gi,
        ''
      )
      .replace(/\s+/g, ' ')
      .trim();
  }
  return { rest: trimOrphanTrailingCon(rest), paymentSelection: selection, cashAmount };
}

const PRODUCTISH_TRAILING =
  /\b(papas|burger|hamburguesa|bebida|gaseosa|coca|sprite|fanta|smash|combo|lata|fritas)\b/i;

function isLikelyAddressStreet(streetPart, products, menuData) {
  const part = String(streetPart || '').trim();
  if (!part || part.length < 3) return false;
  if (!/\d{1,5}[a-z]?$/.test(part)) return false;
  if (products?.length && bestCompositeProductMatch(part, products, menuData)) return false;
  if (PRODUCTISH_TRAILING.test(part)) return false;
  return true;
}

function findTrailingAddress(raw, products, menuData) {
  const hit = findTrailingAddressInText(raw);
  if (!hit?.full) return null;
  const streetPart = hit.full.split(',')[0].trim();
  if (!isLikelyAddressStreet(streetPart, products, menuData)) return null;
  return hit;
}

function stripAddressClause(text, products, menuData) {
  const raw = String(text || '')
    .trim()
    .replace(/[,;.]+\s*$/, '');
  if (!raw) return { rest: '', address: null };

  const explicitRx = ADDRESS_EXPLICIT_RX;
  let m = raw.match(explicitRx);
  if (m) {
    const addr = m[1]
      .replace(/\b(?:pago|pagar|abono).+$/i, '')
      .trim()
      .replace(/[,.;]+$/, '');
    if (addr.length >= 5) {
      return {
        rest: raw.slice(0, m.index).trim().replace(/[,;]\s*$/, ''),
        address: addr,
      };
    }
  }

  const trailing = findTrailingAddress(raw, products, menuData);
  if (trailing) {
    return {
      rest: raw.slice(0, trailing.index).trim().replace(/[,;]\s*$/, '').replace(/[,;.]+\s*$/, ''),
      address: trailing.full,
    };
  }

  return { rest: raw, address: null };
}

function parseQtyAndName(segment) {
  const s = String(segment || '').trim();
  const mPiezas = s.match(/^(\d{1,3})\s+(piezas?)$/i);
  if (mPiezas) {
    return { qty: 1, name: `${mPiezas[1]} ${mPiezas[2]}`.trim() };
  }
  const mMult = s.match(/^(\d{1,2})\s*[xX×]\s*(.+)$/);
  if (mMult) {
    return {
      qty: Math.max(1, Math.min(99, Number(mMult[1]) || 1)),
      name: mMult[2].trim(),
    };
  }
  const mNum = s.match(/^(\d{1,2})\s+(.+)$/);
  if (mNum) {
    return { qty: Math.max(1, Math.min(99, Number(mNum[1]) || 1)), name: mNum[2].trim() };
  }
  const mWord = s.match(
    /^(un|una|uno|unos|unas|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\s+(.+)$/i
  );
  if (mWord) {
    const q = WORD_QTY[mWord[1].toLowerCase()] ?? 1;
    return { qty: Math.max(1, Math.min(99, q)), name: mWord[2].trim() };
  }
  return { qty: 1, name: s };
}

function trimSinTailAtChainedProduct(sinBody) {
  return String(sinBody || '')
    .replace(
      /\s+(?:y|mas|más)\s+(?:kids|lata|bebida|nugget|bigmati|hamburguesa|smash|papas)\b.*/i,
      ''
    )
    .replace(
      /\s+(?=(?:dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|un|una|unos|unas|\d{1,2})\s+(?:kids|lata|bebida|nugget|bigmati|hamburguesa|smash|papas)\b).*/i,
      ''
    )
    .trim();
}

function splitOnChainedQtyProducts(segment) {
  const s = String(segment || '').trim();
  if (!s) return [];
  const parts = s.split(CHAINED_SPLIT_RX).map((p) => p.trim()).filter((p) => p.length >= 2);
  return parts.length ? parts : [s];
}

/** Separa productos pegados tras un "sin …" ("sin pepinillo dos kids" → 2 segmentos). */
function peelChainedProductsFromSegment(segment) {
  const s = String(segment || '').trim();
  if (!s) return [];
  const m = s.match(
    /^(.+?\bsin\s+[a-záéíóúñ]+(?:\s+[a-záéíóúñ]+)*)\s+((?:dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|un|una|unos|unas|\d{1,2})\s+.+)$/i
  );
  if (m && CHAINED_QTY_PRODUCT_RX.test(m[2])) {
    return [m[1].trim(), ...splitOnChainedQtyProducts(m[2].trim())];
  }
  return splitOnChainedQtyProducts(s);
}

function productQueryFromSegment(name) {
  const note = extractCustomizationNoteForComposite(name);
  let base = note ? stripCustomizationFromName(name, note) : name;
  base = base.replace(/\s+con\s+papas\b.*$/i, '').trim();
  base = base.replace(/[,;.]+\s*$/, '').trim();
  base = base
    .replace(/^\b(hamburguesas?|burgers?|sándwichs?|sandwichs?|sandwiches?)\b\s+/i, '')
    .trim();
  const bn = normalizeAssistantText(base);
  if (/^(\d+\s+)?latas?\s*(de\s+bebidas?)?$/.test(bn) || /^\d+\s+latas?\b/.test(bn)) {
    base = 'bebida en lata';
  }
  return base;
}

function extractCustomizationNoteForComposite(name) {
  const note = extractCustomizationNote(name);
  if (!note || !/^sin\b/i.test(note)) return note;
  const body = trimSinTailAtChainedProduct(note.replace(/^sin\s+/i, ''));
  return body ? `sin ${body}` : null;
}

function stripCustomizationFromName(name, note) {
  const raw = String(name || '').trim();
  if (!note) return raw.replace(/\(\s*$/, '').trim();
  if (/^sin\b/i.test(note)) {
    return raw.replace(/\bsin\s+.+$/i, '').replace(/\(\s*$/, '').trim();
  }
  const parenCon = raw.match(/^(.+?)\s*\(\s*con\s+.+?\)\s*$/i);
  if (parenCon) return parenCon[1].trim();
  return raw.replace(/\bcon\s+.+$/i, '').replace(/\(\s*$/, '').trim();
}

/** Separa "kids 2 lata de bebidas" en ítems distintos. */
export function splitEmbeddedQuantitySegments(segments) {
  const embedRx =
    /^(?:(?:un|una|unos|unas)\s+)?(.+?)\s+(\d{1,2})\s+(latas?|bebidas?|gaseosas?|jugos?|schops?|cervezas?|chelas?|birras?)(?:\s+de\s+bebidas?)?\s*[,;.]?\s*$/i;
  const out = [];
  for (const seg of segments) {
    const s = String(seg || '').trim().replace(/[,;.]+\s*$/, '');
    const m = s.match(embedRx);
    if (m && m[1].trim().length >= 2) {
      const lead = s.match(/^(un|una|unos|unas)\s+/i);
      out.push(`${lead ? lead[0] : ''}${m[1].trim()}`.trim());
      const tail = `${m[2]} ${m[3]}${/\s+de\s+bebidas?\s*$/i.test(s) ? ' de bebidas' : ''}`.trim();
      out.push(tail);
    } else {
      out.push(s);
    }
  }
  return out.filter((p) => p.length >= 2);
}

/** "2 bigmati una sin pepinillo" → ["1 bigmati", "una bigmati sin pepinillo", …rest] */
function expandQuantityWithOneCustomized(segment, products, menuData) {
  const s = String(segment || '').trim();
  const m = s.match(
    /^(\d{1,2})\s+([a-záéíóúñ][\wáéíóúñ-]+(?:\s+[a-záéíóúñ][\wáéíóúñ-]+)?)\s+una\s+(sin\s+[a-záéíóúñ]+(?:\s+[a-záéíóúñ]+)?)(?=\s+(?:una|un|unos|unas|\d{1,2})\b|$)/i
  );
  if (!m) return null;
  const total = Number(m[1]);
  const productName = m[2].trim();
  const sinClause = m[3].trim();
  if (total < 2 || !bestCompositeProductMatch(productQueryFromSegment(productName), products, menuData)) {
    return null;
  }
  const head = [];
  const plain = total - 1;
  if (plain > 0) head.push(`${plain} ${productName}`);
  head.push(`una ${productName} ${sinClause}`);
  const consumed = s.slice(0, m.index + m[0].length).trim();
  return { head, tail: s.slice(consumed.length).trim() };
}

/** Parte ítems encadenados sin "y": "una kids 3 latas de bebida". */
export function splitChainedProductSegments(segments, products, menuData) {
  const out = [];
  for (const rawSeg of segments) {
    let seg = String(rawSeg || '').trim();
    if (!seg) continue;

    const custom = expandQuantityWithOneCustomized(seg, products, menuData);
    if (custom) {
      out.push(...custom.head);
      seg = custom.tail;
    }
    if (!seg) continue;

    for (const embedded of splitEmbeddedQtyItems(seg)) {
      for (const chunk of splitKidsBeforeDrinkQty(embedded)) {
        const peeled = peelChainedProductsFromSegment(chunk);
        for (let part of peeled) {
          part = String(part || '').trim();
          if (!part) continue;
          out.push(...splitOnChainedQtyProducts(part));
        }
      }
    }
  }
  return out.filter((p) => p.length >= 2);
}

function isLikelyNewSegment(segment, products, menuData) {
  const s = String(segment || '').trim();
  if (!s) return false;
  if (/^sin\b/i.test(s)) return false;
  const qn = normalizeAssistantText(s);
  if (/^\b(bebida|bebidas|gaseosa|gaseosas)\b$/.test(qn)) return false;
  if (NEW_ITEM_PREFIX.test(s)) return true;
  if (PRODUCT_LINE_HINT_RX.test(qn)) return true;
  if (DRINK_SIDE_HINTS.test(s)) {
    if (/\b(coca|sprite|fanta|schop|pepsi|cola|jugo|agua|cerveza)\b/i.test(qn)) {
      return Boolean(bestCompositeProductMatch(productQueryFromSegment(s), products, menuData));
    }
    return false;
  }
  const query = productQueryFromSegment(s);
  return Boolean(bestCompositeProductMatch(query, products, menuData));
}

function splitOnConjunctions(text, staffOrder = false) {
  const cleaned = String(text || '').trim();
  if (!cleaned) return [];
  if (staffOrder) {
    const lineParts = cleaned
      .split(/\n+/)
      .map((p) => p.trim().replace(/^[,;]\s*/, '').replace(/[,;]+\s*$/, ''))
      .filter((p) => p.length >= 2);
    if (lineParts.length > 1) return lineParts;
  }
  const parts = cleaned
    .split(LIST_ITEM_SPLIT_RX)
    .map((p) => p.trim().replace(/^[,;]\s*/, ''))
    .filter((p) => p.length >= 2);
  return parts.length ? parts : [cleaned];
}

/** Parte ítems pegados sin coma/y: "2 classic grill 1 kids". */
function splitEmbeddedQtyItems(segment) {
  const s = String(segment || '').trim();
  if (!s) return [];
  const parts = s.split(EMBEDDED_QTY_SPLIT_RX).map((p) => p.trim()).filter((p) => p.length >= 2);
  return parts.length > 1 ? parts : [s];
}

function smartSplitOrderSegments(text, products, menuData, staffOrder = false) {
  const cleaned = String(text || '').replace(ORDER_VERB_PREFIX_RX, '').trim();
  if (!cleaned) return [];

  const rawParts = splitOnConjunctions(cleaned, staffOrder);
  if (rawParts.length <= 1) return rawParts.length ? rawParts : [cleaned];

  const merged = [];
  let buf = rawParts[0];
  for (let i = 1; i < rawParts.length; i += 1) {
    const part = rawParts[i];
    if (isLikelyNewSegment(part, products, menuData)) {
      merged.push(buf);
      buf = part;
    } else {
      buf = `${buf} y ${part}`;
    }
  }
  merged.push(buf);
  return merged;
}

function splitKidsBeforeDrinkQty(segment) {
  const s = String(segment || '').trim();
  const m = s.match(/^(.+?)\s+(kids\s+\d{1,2}\s+latas?(?:\s+de\s+bebidas?)?.*)$/i);
  if (m && m[1].trim().length >= 4 && !/\bkids\b/i.test(m[1])) {
    return [m[1].trim(), m[2].trim()];
  }
  return [s];
}

function splitProductAndInlineAddOn(segment, catalog) {
  const s = String(segment || '').trim();
  if (!s) return { productPart: s, inlineAddOn: null };

  const parenCon = s.match(/^(.+?)\s*\(\s*(con\s+.+?)\s*\)\s*$/i);
  if (parenCon) {
    const addonQuery = parenCon[2].trim();
    if (looksLikeMenuAddOnLine(addonQuery, catalog)) {
      const addon = bestCompositeAddOnMatch(addonQuery, catalog);
      if (addon) {
        return { productPart: parenCon[1].trim(), inlineAddOn: { addon, qty: 1 } };
      }
    }
  }

  const patterns = [
    /^(.+?)\s+con\s+(extra\s+.+)$/i,
    /^(.+?)\s+(extra\s+.+)$/i,
    /^(.+?)\s+con\s+(.+)$/i,
  ];

  for (const rx of patterns) {
    const m = s.match(rx);
    if (!m || m[1].trim().length < 2) continue;
    const addonQuery = m[2].trim();
    if (!looksLikeMenuAddOnLine(addonQuery, catalog)) continue;
    const addon = bestCompositeAddOnMatch(addonQuery, catalog);
    if (addon) {
      return { productPart: m[1].trim(), inlineAddOn: { addon, qty: 1 } };
    }
  }

  return { productPart: s, inlineAddOn: null };
}

function addOnKeysCompatible(hostName, addonName) {
  const host = normalizeAssistantText(hostName);
  const target = normalizeAssistantText(addonName);
  if (!host || !target) return false;
  if (host === target) return true;
  const hostCore = host.replace(/^extra\s+/, '');
  const targetCore = target.replace(/^extra\s+/, '');
  if (hostCore === targetCore) return true;
  if (hostCore.length >= 4 && targetCore.includes(hostCore)) return true;
  if (targetCore.length >= 4 && hostCore.includes(targetCore)) return true;
  const tokens = targetCore.split(/\s+/).filter((t) => t.length >= 3);
  return tokens.length > 0 && tokens.every((t) => hostCore.includes(t));
}

function resolveHostedAddOn(product, addon) {
  const offered = collectMenuAddOns([product]);
  const hit = offered.find((a) => addOnKeysCompatible(a.name, addon.name));
  return hit || addon;
}

function attachAddOnToLines(lines, addon, qty, catalog = null) {
  if (!addon || !lines?.length) return false;

  if (catalog) {
    const owners = productsOfferingAddOn(addon, catalog);
    if (owners.length === 1) {
      const ownerId = owners[0].id;
      const host = [...lines].reverse().find((line) => line.product?.id === ownerId);
      if (host) {
        const resolved = resolveHostedAddOn(host.product, addon);
        host.addOns = host.addOns || [];
        for (let i = 0; i < qty; i += 1) {
          host.addOns.push({ name: resolved.name, price: resolved.price || 0, quantity: 1 });
        }
        return true;
      }
    }
  }

  const host = [...lines].reverse().find((line) => {
    const offered = collectMenuAddOns([line.product]);
    return offered.some((a) => addOnKeysCompatible(a.name, addon.name));
  });
  if (!host) return false;
  const resolved = resolveHostedAddOn(host.product, addon);
  host.addOns = host.addOns || [];
  for (let i = 0; i < qty; i += 1) {
    host.addOns.push({ name: resolved.name, price: resolved.price || 0, quantity: 1 });
  }
  return true;
}

function flushPendingAddOns(lines, pendingAddOns, catalog = null) {
  const remaining = [];
  for (const { addon, qty } of pendingAddOns) {
    if (!attachAddOnToLines(lines, addon, qty, catalog)) {
      remaining.push({ addon, qty });
    }
  }
  return remaining;
}

function resolveRemoved(product, note) {
  if (!note || !/^sin\b/i.test(note)) return [];
  const raw = note.replace(/^sin\s+/i, '').trim();
  const parts = raw
    .split(/\s+y\s+sin\s+|\s*,\s*|\s+y\s+/i)
    .map((p) => p.replace(/^sin\s+/i, '').trim())
    .filter(Boolean);
  const removable = (product?.removable_ingredients || []).map((x) =>
    typeof x === 'string' ? x : x?.name || ''
  );
  if (!removable.length) return parts;
  return parts
    .map((p) => {
      const pn = normalizeAssistantText(p);
      const hit = removable.find(
        (r) => normalizeAssistantText(r).includes(pn) || pn.includes(normalizeAssistantText(r))
      );
      return hit || p;
    })
    .filter(Boolean);
}

/**
 * @param {string} text
 * @param {object[]} products
 * @param {object|null} [menuData] — carta del local (productos, promos, categorías)
 * @param {{ staffOrder?: boolean }} [options]
 * @returns {{ lines: { product: object, qty: number, removedIngredients: string[], note: string|null }[], paymentSelection: string|null, cashAmount: number|null, address: string|null, tip: number|null, tipPercent: number|null, unmatchedNotes: string[] }|null}
 */
export function parseCompositeOrderText(text, products, menuData = null, options = {}) {
  const raw = normalizeCompositeOrderInput(text, options);
  if (!raw || !products?.length) return null;
  if (!hasCompositeOrderIntent(raw) && !options.staffOrder) {
    return null;
  }

  const staffNl = Boolean(options.staffOrder);
  const tipInput = staffNl ? preserveStaffNewlines(raw) : raw;
  const { rest: afterTip, tip, tipPercent } = stripTipClause(tipInput);
  const { rest: afterPay, paymentSelection, cashAmount } = stripPaymentClause(afterTip);
  const payableRest = staffNl ? restoreStaffNewlines(afterPay) : afterPay;
  const { rest, address } = options.staffOrder
    ? { rest: payableRest, address: null }
    : stripAddressClause(afterPay, products, menuData);
  const catalog = (menuData?.promos || []).length
    ? expandCatalogForCompositeOrder(products, menuData)
    : expandProductsForCompositeOrder(products);
  const segments = splitChainedProductSegments(
    splitEmbeddedQuantitySegments(
      smartSplitOrderSegments(rest, catalog, menuData, Boolean(options.staffOrder))
    ),
    catalog,
    menuData
  );
  if (!segments.length) return null;

  const lines = [];
  const unmatchedNotes = [];
  const pendingAddOns = [];

  for (const seg of segments) {
    const { productPart, inlineAddOn } = splitProductAndInlineAddOn(seg, catalog);
    const { qty, name } = parseQtyAndName(productPart);
    const note = extractCustomizationNoteForComposite(name);
    const query = productQueryFromSegment(name);
    const bestProduct = bestCompositeProductMatch(query, catalog, menuData);

    if (!bestProduct) {
      const addonQuery = looksLikeMenuAddOnLine(seg, catalog) ? seg : query;
      const addon = bestCompositeAddOnMatch(addonQuery, catalog);
      if (addon) {
        if (lines.length && attachAddOnToLines(lines, addon, qty, catalog)) {
          continue;
        }
        pendingAddOns.push({ addon, qty });
        continue;
      }
      if (DRINK_SIDE_HINTS.test(name) || note || name.length >= 4) {
        unmatchedNotes.push(note || name);
      }
      continue;
    }

    const removedIngredients = resolveRemoved(bestProduct, note);
    let lineNote = null;
    if (note) {
      if (/^sin\b/i.test(note)) {
        if (!removedIngredients.length) lineNote = note;
      } else {
        lineNote = note;
      }
    }

    lines.push({
      product: bestProduct,
      qty,
      removedIngredients,
      note: lineNote,
      addOns: [],
    });

    if (inlineAddOn?.addon) {
      attachAddOnToLines(lines, inlineAddOn.addon, inlineAddOn.qty, catalog);
    }

    const stillPending = flushPendingAddOns(lines, pendingAddOns, catalog);
    pendingAddOns.length = 0;
    pendingAddOns.push(...stillPending);
  }

  const leftoverAddOns = flushPendingAddOns(lines, pendingAddOns, catalog);
  for (const { addon } of leftoverAddOns) {
    unmatchedNotes.push(addon.name);
  }

  if (!lines.length && !unmatchedNotes.length && !address && !paymentSelection && tip == null && tipPercent == null) {
    return null;
  }
  return { lines, paymentSelection, cashAmount, address, tip, tipPercent, unmatchedNotes };
}

export function paymentSelectionIsValid(selection, business) {
  if (!selection) return false;
  const { canonical } = selectionToCanonicalPayment(selection);
  if (canonical === 'efectivo') return business?.accepts_cash !== false;
  if (canonical === 'transferencia') return business?.accepts_transfer !== false;
  return business?.accepts_card === true || business?.accepts_mercadopago === true;
}
